
import { ScenarioType } from "@/types/analysis";
import { Button } from "@/components/ui/button";
import { ToolUsageChecklist } from "./ToolUsageChecklist";
import ReactMarkdown from "react-markdown";
import { cn } from "@/lib/utils";
import { Sparkles, Info, AlertTriangle, Plus, FileImage, Brain, Lightbulb, ExternalLink, BookOpen, Loader2 } from "lucide-react";
import { TermsAndConditionsDialog } from "./TermsAndConditionsDialog";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffect, useState } from "react";

interface AnalysisContentProps {
  activeScenario: ScenarioType | null;
  isAnalyzing: boolean;
  loadingMessage: string;
  analysisResult: string | null;
  onAnalyze: () => void;
  usedTools: {
    draw: boolean;
    incision: boolean;
    ruler: boolean;
    angle: boolean;
  };
  error?: string | null;
  onAddClick?: () => void;
  onStartGuide?: () => void;
  isExpanded?: boolean;
}

export const AnalysisContent = ({
  activeScenario,
  isAnalyzing,
  loadingMessage,
  analysisResult,
  onAnalyze,
  usedTools,
  error,
  onAddClick,
  onStartGuide,
  isExpanded = false
}: AnalysisContentProps) => {
  const [isReanalyzing, setIsReanalyzing] = useState(false);

  // Reset reanalyzing state when analysis completes or errors
  useEffect(() => {
    if (!isAnalyzing && isReanalyzing) {
      setIsReanalyzing(false);
    }
  }, [isAnalyzing]);

  const handleReanalyze = () => {
    setIsReanalyzing(true);
    onAnalyze();
  };

  if (!activeScenario) {
    return (
      <div className={cn(
        "flex flex-col items-center justify-center text-center space-y-5 py-3",
        isExpanded && "py-6 space-y-6"
      )}>
        <div className="relative">
          <div className={cn(
            "rounded-full bg-gradient-to-br from-indigo-100/90 to-blue-50/90 flex items-center justify-center shadow-md",
            isExpanded ? "w-16 h-16" : "w-14 h-14"
          )}>
            <div className="absolute w-full h-full rounded-full bg-gradient-to-r from-indigo-300/20 to-purple-300/20 animate-pulse"></div>
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400/10 via-indigo-400/10 to-purple-400/10 animate-gradient-xy"></div>
            <Sparkles className={cn(
              "text-indigo-400 drop-shadow-md",
              isExpanded ? "w-8 h-8" : "w-6 h-6"
            )} />
          </div>
        </div>
        
        <div className={cn(
          "space-y-2 max-w-[300px] mx-auto",
          isExpanded && "space-y-3 max-w-[320px]"
        )}>
          <h3 className={cn(
            "font-semibold bg-gradient-to-r from-indigo-600 to-blue-500 text-transparent bg-clip-text",
            isExpanded ? "text-lg" : "text-base"
          )}>
            No Scenario Selected
          </h3>
          
          <p className={cn(
            "text-gray-600 leading-relaxed",
            isExpanded ? "text-xs" : "text-[11px]"
          )}>
            Add a surgical scenario to unlock AI-powered analysis of your drawings
          </p>

          <div className="grid grid-cols-2 gap-2 mt-1">
            <div className={cn(
              "bg-indigo-50/80 backdrop-blur-sm rounded-lg p-2 text-left",
              isExpanded && "p-3"
            )}>
              <Brain className={cn(
                "text-indigo-400 mb-1",
                isExpanded ? "h-4 w-4" : "h-3.5 w-3.5"
              )} />
              <p className={cn(
                "text-indigo-700 font-medium",
                isExpanded ? "text-xs" : "text-[10px]"
              )}>
                AI Analysis
              </p>
            </div>
            <div className={cn(
              "bg-indigo-50/80 backdrop-blur-sm rounded-lg p-2 text-left",
              isExpanded && "p-3"
            )}>
              <Lightbulb className={cn(
                "text-amber-400 mb-1",
                isExpanded ? "h-4 w-4" : "h-3.5 w-3.5"
              )} />
              <p className={cn(
                "text-indigo-700 font-medium",
                isExpanded ? "text-xs" : "text-[10px]"
              )}>
                Smart Insights
              </p>
            </div>
          </div>
          
          <Button 
            onClick={onAddClick} 
            className={cn(
              "relative w-full justify-center gap-1.5 overflow-hidden group mt-2", 
              "bg-gradient-to-r from-indigo-500 via-blue-500 to-indigo-600", 
              "text-white shadow-lg hover:shadow-xl", 
              "border border-indigo-300/50", 
              "transition-all duration-300 ease-out", 
              "hover:scale-[1.02]",
              "text-xs h-8",
              isExpanded && "mt-3 h-9"
            )} 
            size="sm"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-indigo-400/0 via-white/30 to-indigo-400/0 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></span>
            <Plus className="h-3.5 w-3.5" />
            <span>Add Scenario</span>
          </Button>
        </div>
      </div>
    );
  }

  if (analysisResult && !isAnalyzing && !isReanalyzing) {
    return (
      <div className={cn(
        "space-y-3 py-2",
        isExpanded && "py-3 space-y-4"
      )}>
        <div className={cn(
          "prose prose-sm max-w-none",
          "bg-white/90 backdrop-blur-sm rounded-lg border border-indigo-100/60 shadow-sm",
          isExpanded ? "p-4" : "p-3"
        )}>
          <ReactMarkdown className="text-gray-700 text-sm leading-relaxed">
            {analysisResult}
          </ReactMarkdown>
          
          <div className="flex justify-end mt-2">
            <Button 
              onClick={handleReanalyze} 
              variant="outline"
              size="sm"
              className={cn(
                "text-xs gap-1.5 text-indigo-600 border-indigo-200 hover:bg-indigo-50",
                isExpanded && "text-sm"
              )}
            >
              <Sparkles className="h-3 w-3" />
              Reanalyze
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isAnalyzing || isReanalyzing) {
    return (
      <div className={cn(
        "space-y-2 py-2", 
        isExpanded && "py-3 space-y-3"
      )}>
        <div className={cn(
          "flex flex-col gap-3 bg-gradient-to-br from-indigo-50/70 to-blue-50/70 backdrop-blur-sm rounded-lg border border-indigo-100/60 shadow-sm",
          isExpanded ? "p-4" : "p-3"
        )}>
          <div className="flex items-center gap-2">
            <div className={cn(
              "rounded-full bg-gradient-to-br from-indigo-500 to-blue-500 animate-pulse shadow-md",
              isExpanded ? "w-4 h-4" : "w-3.5 h-3.5"
            )}></div>
            <p className={cn(
              "text-indigo-700 font-medium animate-pulse",
              isExpanded ? "text-xs" : "text-[11px]"  
            )}>
              {loadingMessage || (isReanalyzing ? "Reanalyzing..." : "Analyzing...")}
            </p>
          </div>
          
          <div className="space-y-2.5">
            <Skeleton className="w-full h-4 bg-indigo-100/60" />
            <Skeleton className="w-[85%] h-4 bg-indigo-100/60" />
            <Skeleton className="w-[90%] h-4 bg-indigo-100/60" />
            <Skeleton className="w-[80%] h-4 bg-indigo-100/60" />
            <Skeleton className="w-[60%] h-4 bg-indigo-100/60" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(
        "space-y-3 py-2",
        isExpanded && "py-3 space-y-4"
      )}>
        <Alert variant="destructive" className={cn(
          "bg-red-50/80 backdrop-blur-sm text-red-800 border-red-200/80 shadow-sm",
          isExpanded && "p-4"
        )}>
          <AlertTriangle className={isExpanded ? "h-4 w-4" : "h-3.5 w-3.5"} />
          <AlertDescription className={isExpanded ? "text-xs" : "text-[11px]"}>
            {error}
          </AlertDescription>
        </Alert>
        <Button 
          onClick={onAnalyze} 
          className={cn(
            "w-full bg-gradient-to-r from-indigo-500/90 to-blue-500/90 hover:from-indigo-600 hover:to-blue-600 text-white transform transition-all duration-300 hover:scale-[1.02] hover:shadow-md",
            "text-xs h-8",
            isExpanded && "h-9"
          )}
          size="sm"
        >
          Try Again
        </Button>
      </div>
    );
  }

  const completedSteps = Object.values(usedTools).filter(Boolean).length;
  const isReady = completedSteps > 0;

  return (
    <div className={cn(
      "space-y-3 py-1",
      isExpanded && "space-y-4 py-2"
    )}>
      <ToolUsageChecklist 
        usedTools={usedTools} 
        isExpanded={isExpanded}
      />
      
      <div className="space-y-3">
        <Separator className="bg-gradient-to-r from-indigo-100/50 via-indigo-200/60 to-indigo-100/50" />
        
        <div className="space-y-2">
          <Button 
            onClick={onAnalyze} 
            className={cn(
              "w-full transition-all duration-300 gap-1.5", 
              "bg-gradient-to-r from-indigo-500/90 to-blue-500/90", 
              "text-white hover:from-indigo-600 hover:to-blue-600", 
              "shadow-md hover:shadow-lg transform hover:-translate-y-0.5 hover:scale-[1.02]", 
              "border border-white/10", 
              "relative overflow-hidden group", 
              "text-xs h-8",
              !isReady && "opacity-50 cursor-not-allowed",
              isExpanded && "h-9"
            )} 
            size="sm"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-indigo-400/0 via-white/20 to-indigo-400/0 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></span>
            <Sparkles className={isExpanded ? "h-4 w-4" : "h-3.5 w-3.5"} />
            Generate AI Analysis
          </Button>
          
          <div className="text-center">
            <p className="text-[9px] text-gray-400">
              By clicking "Generate AI Analysis" you have read and accept the <TermsAndConditionsDialog />
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
